import { createApi } from '@reduxjs/toolkit/query/react'
import baseQuery from './base-query.js'

/**
 * The client-visible result of minting an access link.
 *
 * Declared here rather than imported from `@tunasong/access-links`: that is a backend package and
 * `lib/redux` does not — and should not — depend on one. Only the fields a client consumes are
 * listed; the wire response carries more.
 */
export interface CreatedAccessLink {
  /**
   * The feed URL, token included.
   *
   * @important This is the ONLY response in the whole access-link surface that carries the token.
   * The store keeps a hash, so it can never be retrieved again — a caller that does not put this
   * in front of the user has lost it, and the only remedy is minting another link.
   */
  url: string
  link: {
    linkId: string
    name: string
    createdAt: string
  }
}

/**
 * Access-link management (GH #1853).
 *
 * Mounted at the `links` mapping key (`access-links-stack.ts`), so the route is
 * `POST {apiBaseUrl}/links/{entityId}`. These are ordinary JWT routes — a user manages their own
 * links while logged in. Only the calendar feed itself consumes the link authorizer.
 *
 * Create only, deliberately. Listing, renaming, regenerating and revoking all exist server-side
 * and belong to the link management panel, which is still Joni's spec — see #1853.
 */
export const accessLinksApi = createApi({
  reducerPath: 'access-links-api',
  baseQuery,
  endpoints: builder => ({
    createAccessLink: builder.mutation<CreatedAccessLink, { entityId: string; name: string }>({
      query: ({ entityId, name }) => ({
        url: `links/${entityId}`,
        method: 'POST',
        body: { name },
      }),
    }),
  }),
})

export const { useCreateAccessLinkMutation } = accessLinksApi
